/**
 * The globals helper reads global variables, that were loaded before the app start:
 * index.html loads the window.GLOBALS and window.SESSION vars before all other things.
 *
 * This file is part of Minasa
 * (c) Kaden und Partner AG
 */
import { Session } from '@/@types/globals';
import { IUser } from '@/@types/IUser';
import dayjs, { type Dayjs } from 'dayjs';
import qs from 'qs';

/**
 * Returns a global variable value from window.GLOBALS
 * @param {string} key The global var name
 * @returns the value or null
 */
export function globalVar(key: string) {
    const globals = window.GLOBALS;
    return globals[key];
}

/**
 * Returns a function to get an entry from GLOBALS.params, with a default if the param does not exists.
 *
 * @param {string} key The global var name
 * @param {string} defaultValue The default value, should the param not exist
 * @returns the value or the default
 */
export function globalParam(key: string, defaultValue: any = null) {
    const params = globalVar('params') || {};
    if (Object.keys(params).indexOf(key) >= 0) {
        return params[key];
    }
    return defaultValue;
}

export function customer(): string {
    return globalParam('customer') || '';
}

/**
 * Returns the window.SESSION session info object
 */
export function sessionInfo(): Session {
    return (
        window.SESSION ||
        ({
            csrfToken: '',
            session_id: '',
            user: {} as IUser,
        } as Session)
    );
}

/**
 * Returns the window.SESSION.user object, or an empty object.
 */
export function userInfo(): IUser {
    return sessionInfo().user || {};
}

/**
 * forms a complete backend API url from a relative URL,
 * e.g. relUrl = '/login' => 'https://domain.com/services/login
 * @param {string} relUrl
 * @param {string|object} query Query part of the URL, either as string or key/value pairs
 * @returns {string} The fully formed URL
 */
export function apiUrl(relUrl: string, query?: string | object) {
    return formUrl(window.apiRoot, relUrl, query);
}

/**
 * forms a complete frontend url from a relative URL,
 * e.g. relUrl = '/magazin' => 'https://domain.com/magazin
 * @param {string} relUrl
 * @param {string|object} query Query part of the URL, either as string or key/value pairs
 * @returns {string} The fully formed URL
 */
export function frontendUrl(relUrl: string, query?: string | { [key: string]: any }): string {
    const host = globalVar('webhost');
    const webroot = globalVar('webroot');
    return formUrl(`${host}${webroot}`, relUrl, query);
}

/**
 * forms a complete Frontend app url from a relative URL,
 * e.g. relUrl = '/locale/de.json' => 'https://domain.com/admin-cockpit/locale/de.json'
 * @param {string} relUrl
 * @param {string|object} query Query part of the URL, either as string or key/value pairs
 * @returns {string} The fully formed URL
 */
export function appUrl(relUrl: string, query?: string | object): string {
    return formUrl(window.appRoot, relUrl, query);
}

function formUrl(baseUrl: string, relUrl: string, query?: string | object): string {
    // remove leading slash:
    relUrl = (relUrl || '').replace(/^\//, '');
    // remove trailing slash:
    const root = baseUrl.replace(/\/$/, '');
    let url = `${root}/${relUrl}`;

    if (query) {
        url += url.match(/\?/) ? '&' : '?';
        if (typeof query === 'string') {
            url += query;
        } else if (typeof query === 'object') {
            url += qs.stringify(stringifyDayjs(query));
        }
    }
    return url;
}

function stringifyDayjs(value: any) {
    if (value instanceof dayjs) {
        value = (value as Dayjs).toISOStringIf();
    } else if (typeof value === 'object' || Array.isArray(value)) {
        for (const key in value) {
            value[key] = stringifyDayjs(value[key]);
        }
    }
    return value;
}

export function tagDownloadUrl(options?: object): string {
    options = options || {};
    return apiUrl('/tag/list', {
        output: 'html',
        ...options,
    });
}

export function kulturplatzModuleEnabled(): boolean {
    return globalParam('kulturplatz.show');
}
